import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import KnotDecoration from '../components/illustrations/KnotDecoration';
import { BlogAPI } from '../api/api';

const stripHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

function ReadingProgress() {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const updateProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const readProgress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setProgress(Math.min(100, Math.max(0, readProgress)));
    };

    window.addEventListener('scroll', updateProgress);
    return () => window.removeEventListener('scroll', updateProgress);
  }, []);

  return (
    <div className="fixed top-0 left-0 right-0 h-1 z-[60] bg-[var(--color-inerva-mint)]/30">
      <div
        className="h-full bg-[var(--color-inerva-forest)] transition-all duration-150"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
}

export default function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const [article, setArticle] = useState(null);
  const [allArticles, setAllArticles] = useState([]);
  const [prevArticle, setPrevArticle] = useState(null);
  const [nextArticle, setNextArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const BASE_PUBLIC_BLOG_URL = 'https://www.inervapsicologia.es/blog/';

  useEffect(() => {
    let mounted = true;
    const fetchArticle = async () => {
      setLoading(true);
      setError('');
      try {
        const data = await BlogAPI.getBySlug(slug);
        if (!mounted) return;
        if (data.imagenPortada) {
          data.imagenPortada = `data:image/jpeg;base64,${data.imagenPortada}`;
        }
        setArticle(data);
      } catch (err) {
        if (!mounted) return;
        setError('No se pudo cargar el artículo.');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchArticle();
    return () => { mounted = false; };
  }, [slug]);

  useEffect(() => {
    let mounted = true;
    const fetchAll = async () => {
      try {
        const data = await BlogAPI.getAll();
        if (!mounted) return;
        setAllArticles(data);

        const sorted = data.slice().sort((a, b) => Number(a.id) - Number(b.id));
        if (article && article.id != null) {
          const idx = sorted.findIndex((a) => Number(a.id) === Number(article.id));
          setPrevArticle(idx > 0 ? sorted[idx - 1] : null);
          setNextArticle(idx >= 0 && idx < sorted.length - 1 ? sorted[idx + 1] : null);

          const related = data
            .filter((a) => a.categoria === article.categoria && Number(a.id) !== Number(article.id))
            .slice(0, 3);
          setRelatedArticles(related);
        }
      } catch (err) {}
    };
    if (article) fetchAll();
    return () => { mounted = false; };
  }, [article]);

  const goToArticle = (targetSlug) => {
    if (!targetSlug) return;
    navigate(`/blog/${targetSlug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleShare = async () => {
    const url = BASE_PUBLIC_BLOG_URL + slug;
    try {
      if (navigator.share) {
        await navigator.share({ title: article?.titulo || 'Artículo', text: article?.titulo || '', url });
      } else {
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      }
    } catch (err) {}
  };

  const handleCopyLink = async () => {
    const url = BASE_PUBLIC_BLOG_URL + slug;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert('No se pudo copiar el enlace.');
    }
  };

  const handleWhatsApp = () => {
    const url = encodeURIComponent(BASE_PUBLIC_BLOG_URL + slug);
    const text = encodeURIComponent(article?.titulo ? `${article.titulo} — ` : '') + url;
    window.open(`https://wa.me/?text=${text}`, '_blank');
  };

  const getMetaDesc = () => {
    if (!article) return '';
    if (article.metaDescripcion) return article.metaDescripcion;
    if (article.extracto) return article.extracto;
    return stripHtml(article.contenido || '').substring(0, 160);
  };

  const getJsonLd = () => {
    if (!article) return null;
    return {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: article.titulo,
      description: getMetaDesc(),
      image: article.imagenPortada || undefined,
      datePublished: article.fechaPublicacion,
      dateModified: article.fechaActualizacion || article.fechaPublicacion,
      author: {
        '@type': 'Organization',
        name: 'Inerva Psicología',
        url: 'https://www.inervapsicologia.es',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Inerva Psicología',
        url: 'https://www.inervapsicologia.es',
      },
      mainEntityOfPage: {
        '@type': 'WebPage',
        '@id': `https://www.inervapsicologia.es/blog/${slug}`,
      },
    };
  };

  if (loading) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-cream pt-32 pb-20">
          <div className="container-narrow text-center">
            <div className="inline-flex items-center gap-3 text-[var(--color-inerva-text-light)]">
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Cargando artículo...
            </div>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  if (error || !article) {
    return (
      <>
        <Header />
        <main className="min-h-screen bg-cream pt-32 pb-20">
          <div className="container-narrow text-center">
            <p className="text-red-600 mb-4">{error || 'Artículo no encontrado'}</p>
            <Link to="/blog" className="btn-primary">Volver al blog</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article.titulo} | Inerva Psicología</title>
        <meta name="description" content={getMetaDesc()} />
        <link rel="canonical" href={`https://www.inervapsicologia.es/blog/${slug}`} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.titulo} />
        <meta property="og:description" content={getMetaDesc()} />
        {article.imagenPortada && <meta property="og:image" content={article.imagenPortada} />}
        <script type="application/ld+json">{JSON.stringify(getJsonLd())}</script>
      </Helmet>

      <ReadingProgress />
      <Header />

      <main className="min-h-screen bg-white">
        {article.imagenPortada && (
          <div className="w-full h-64 md:h-96 bg-mint overflow-hidden">
            <img src={article.imagenPortada} alt={article.titulo} className="w-full h-full object-cover" />
          </div>
        )}

        <article className="container-narrow py-12 md:py-20">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-[var(--color-inerva-text-light)] hover:text-[var(--color-inerva-forest)] mb-8 transition-colors"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Volver al blog
            </Link>

            <header className="mb-10">
              <div className="flex items-center gap-3 text-sm text-[var(--color-inerva-text-light)] mb-4">
                {article.categoria && (
                  <span className="px-3 py-1 bg-blush rounded-full">{article.categoria}</span>
                )}
                <span>
                  {new Date(article.fechaPublicacion).toLocaleDateString('es-ES', {
                    day: 'numeric', month: 'long', year: 'numeric',
                  })}
                </span>
                {article.tiempoLectura && (
                  <span className="px-3 py-1 bg-mint rounded-full">
                    {article.tiempoLectura} min de lectura
                  </span>
                )}
              </div>

              <h1 className="text-3xl md:text-4xl lg:text-5xl leading-tight mb-6">
                {article.titulo}
              </h1>

              <div className="flex flex-wrap gap-3">
                <button
                  onClick={handleShare}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-forest text-white rounded-full hover:bg-[var(--color-inerva-forest-light)] transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                  </svg>
                  Compartir
                </button>

                <button
                  onClick={handleCopyLink}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[var(--color-inerva-forest)] text-[var(--color-inerva-forest)] rounded-full hover:bg-forest hover:text-white transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  {copied ? '¡Copiado!' : 'Copiar enlace'}
                </button>

                <button
                  onClick={handleWhatsApp}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-[#25D366] text-white rounded-full hover:bg-[#20bd5a] transition-colors"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                  </svg>
                  WhatsApp
                </button>
              </div>
            </header>

            <div
              className="prose prose-lg max-w-none prose-headings:font-serif prose-headings:text-[var(--color-inerva-forest)] prose-p:text-[var(--color-inerva-text)] prose-a:text-[var(--color-inerva-forest)] prose-strong:text-[var(--color-inerva-forest)]"
              dangerouslySetInnerHTML={{ __html: article.contenido }}
            />

            {/* Author section */}
            <div className="mt-12 p-6 bg-cream rounded-2xl flex items-center gap-5">
              <img
                src="/assets/ana-ruiz.png"
                alt="Ana Ruiz"
                className="w-16 h-16 rounded-full object-cover"
              />
              <div>
                <p className="font-serif text-lg mb-1">Ana Ruiz</p>
                <p className="text-sm text-[var(--color-inerva-text-light)]">
                  Psicóloga General Sanitaria · Fundadora de Inerva Psicología
                </p>
              </div>
            </div>

            {/* Related articles */}
            {relatedArticles.length > 0 && (
              <div className="mt-16">
                <h3 className="text-xl font-serif mb-6">Artículos relacionados</h3>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedArticles.map((rel) => (
                    <Link
                      key={rel.id}
                      to={`/blog/${rel.slug}`}
                      className="group block bg-cream rounded-xl overflow-hidden hover:shadow-md transition-all"
                    >
                      {rel.imagenPortada && (
                        <div className="aspect-[16/10] overflow-hidden">
                          <img
                            src={`data:image/jpeg;base64,${rel.imagenPortada}`}
                            alt={rel.titulo}
                            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                          />
                        </div>
                      )}
                      <div className="p-4">
                        <p className="font-serif group-hover:text-[var(--color-inerva-forest-light)] transition-colors">
                          {rel.titulo}
                        </p>
                        {rel.tiempoLectura && (
                          <span className="text-xs text-[var(--color-inerva-text-light)]">
                            {rel.tiempoLectura} min de lectura
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-16 pt-8 border-t border-[var(--color-inerva-mint)]">
              <KnotDecoration size="sm" className="mx-auto mb-8 opacity-30" />

              <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                <div className="flex gap-3">
                  {prevArticle ? (
                    <button
                      onClick={() => goToArticle(prevArticle.slug)}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[var(--color-inerva-forest)] text-[var(--color-inerva-forest)] rounded-full hover:bg-forest hover:text-white transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Anterior
                    </button>
                  ) : (
                    <button disabled className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 text-gray-300 rounded-full cursor-not-allowed">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                      Anterior
                    </button>
                  )}

                  {nextArticle ? (
                    <button
                      onClick={() => goToArticle(nextArticle.slug)}
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-[var(--color-inerva-forest)] text-[var(--color-inerva-forest)] rounded-full hover:bg-forest hover:text-white transition-colors"
                    >
                      Siguiente
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  ) : (
                    <button disabled className="inline-flex items-center gap-2 px-4 py-2 text-sm border border-gray-200 text-gray-300 rounded-full cursor-not-allowed">
                      Siguiente
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  )}
                </div>

                <Link to="/blog" className="btn-primary">
                  Ver todos los artículos
                </Link>
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />

      {copied && (
        <div className="fixed bottom-6 right-6 z-50 px-4 py-3 bg-forest text-white rounded-lg shadow-lg animate-fadeIn">
          Enlace copiado ✓
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn { animation: fadeIn 0.3s ease-out; }
      `}</style>
    </>
  );
}
